var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { faQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormControlLabel, Grid, Radio, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography, styled } from '@mui/material';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PlanType } from '../../enum';
import { Autocomplete, FormField, RadioGroup } from '../common';
import { CUSTOM_WIDTH_AUTOCOMPLETE, LIMIT_TIMELINE } from './constants';
import { ACTUAL_ATTRITION_MODE, FREQUENCY_ENUM, METRICS_ENUM, PERIOD_KEY } from './enum';
import { createParamsFetchActual, useActualReportAPI, useSelectDisabledDaily, useTimelineSync } from './selectors';
var StyledIcon = styled(FontAwesomeIcon)(function (_a) {
  var theme = _a.theme;
  return {
    width: '0.825rem',
    height: '0.825rem',
    color: theme.palette.common.black,
    border: '0.0625rem solid',
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.warning.light,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    padding: '0.25rem',
    '& path': {
      opacity: '1 !important'
    }
  };
});
var StyledToggleButtonGroup = styled(ToggleButtonGroup)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'grid',
    width: 'max-content',
    gridTemplateColumns: 'repeat(2, minmax(4.625rem, 1fr))',
    '& .MuiButtonBase-root': {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.main,
      height: '2rem',
      fontWeight: 'normal',
      fontSize: '0.875rem'
    },
    '& .Mui-selected': {
      zIndex: 1,
      color: theme.palette.common.black,
      backgroundColor: theme.palette.primary.light + '!important',
      borderColor: theme.palette.primary.main + '!important'
    }
  };
});
var StyledTooltip = styled(function (_a) {
  var className = _a.className,
    props = __rest(_a, ["className"]);
  return _jsx(Tooltip, __assign({}, props, {
    classes: {
      popper: className
    },
    arrow: true
  }));
})(function (_a) {
  var _b;
  var theme = _a.theme;
  return _b = {}, _b["& .MuiTooltip-tooltip"] = {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    border: '0.0625rem solid',
    borderColor: theme.palette.warning.main,
    fontSize: '0.875rem',
    padding: '0.375rem 0.875rem',
    boxShadow: 'none'
  }, _b["& .MuiTooltip-arrow"] = {
    color: theme.palette.common.white,
    '&:before': {
      border: '0.0625rem solid',
      borderColor: theme.palette.warning.main,
      boxSizing: 'border-box'
    }
  }, _b;
});
var StyledStack = styled(Stack)(function (_a) {
  var theme = _a.theme;
  return {
    flexDirection: 'row',
    padding: theme.spacing(0, 3),
    gap: theme.spacing(6),
    flexWrap: 'wrap'
  };
});
var StyledTypographyTitle = styled(Typography)(function (_a) {
  var theme = _a.theme;
  return {
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(28)
  };
});
var StyledGridFilters = styled(Grid)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2)
  };
});
export var DataPanelFilters = function (_a) {
  var timelines = _a.timelines,
    planType = _a.planType;
  var tMetric = useTranslation('report', {
    keyPrefix: 'actual.metrics'
  }).t;
  var t = useTranslation('report', {
    keyPrefix: 'actual'
  }).t;
  var _b = useFormContext(),
    watch = _b.watch,
    setValue = _b.setValue,
    control = _b.control,
    handleSubmit = _b.handleSubmit;
  var _c = watch(),
    metricTab = _c.metricTab,
    frequency = _c.frequency,
    periodStart = _c.periodStart,
    periodEnd = _c.periodEnd,
    actualAttritionMode = _c.actualAttritionMode;
  var dailyDisabled = useSelectDisabledDaily();
  var _d = useActualReportAPI(),
    fetchActualReport = _d[1],
    clearActualReport = _d[2];
  var handleFetchActualReport = handleSubmit(function (formData) {
    clearActualReport();
    fetchActualReport({}, createParamsFetchActual(formData));
  });
  var isMonthlyPlanType = useMemo(function () {
    return [PlanType.Monthly, PlanType.MonthlyCustom].includes(planType);
  }, [planType]);
  var isLimitedTimeline = useMemo(function () {
    return planType === PlanType.Weekly && frequency === FREQUENCY_ENUM.DAILY;
  }, [planType, frequency]);
  var isShowEndTimeLine = useMemo(function () {
    return isMonthlyPlanType && frequency === FREQUENCY_ENUM.PERIOD || planType === PlanType.Weekly;
  }, [planType, frequency, isMonthlyPlanType]);
  var handleTimelineChange = useTimelineSync({
    timelines: timelines,
    periodStart: periodStart,
    periodEnd: periodEnd,
    isLimitedTimeline: isLimitedTimeline,
    isShowEndTimeLine: isShowEndTimeLine,
    setValue: setValue
  });
  return _jsxs(StyledStack, {
    children: [_jsx(StyledTypographyTitle, {
      children: tMetric(metricTab)
    }), _jsxs(StyledGridFilters, __assign({
      container: true,
      sx: {
        flexWrap: {
          xs: 'wrap',
          md: 'nowrap'
        }
      }
    }, {
      children: [_jsxs(Stack, __assign({
        direction: "row",
        gap: 3,
        alignItems: "center"
      }, {
        children: [_jsx(FormField, __assign({
          required: true,
          name: "periodStart",
          control: control
        }, {
          children: _jsx(Autocomplete, {
            id: "periodStart",
            sx: {
              minWidth: CUSTOM_WIDTH_AUTOCOMPLETE[planType]
            },
            options: timelines,
            getOptionValue: function (o) {
              return o.id;
            },
            onChange: function (id) {
              handleTimelineChange(PERIOD_KEY.START, id);
              handleFetchActualReport();
            }
          })
        })), isShowEndTimeLine && _jsxs(_Fragment, {
          children: [_jsx(Typography, __assign({
            fontSize: "0.875rem"
          }, {
            children: t('to')
          })), _jsx(FormField, __assign({
            required: true,
            name: "periodEnd",
            control: control
          }, {
            children: _jsx(Autocomplete, {
              id: "periodEnd",
              sx: {
                minWidth: CUSTOM_WIDTH_AUTOCOMPLETE[planType]
              },
              options: timelines,
              getOptionValue: function (o) {
                return o.id;
              },
              onChange: function (id) {
                handleTimelineChange(PERIOD_KEY.END, id);
                handleFetchActualReport();
              }
            })
          }))]
        }), isLimitedTimeline && _jsx(StyledTooltip, __assign({
          title: t('helpDescription', {
            limit: LIMIT_TIMELINE
          }),
          placement: "top",
          arrow: true
        }, {
          children: _jsx("div", __assign({
            "data-testid": "help-icon"
          }, {
            children: _jsx(StyledIcon, {
              icon: faQuestion
            })
          }))
        }))]
      })), _jsxs(Stack, __assign({
        direction: "row",
        gap: 3,
        alignItems: "center"
      }, {
        children: [metricTab === METRICS_ENUM.ATTRITION && _jsxs(RadioGroup, __assign({
          row: true,
          value: actualAttritionMode,
          onChange: function (_e, newMode) {
            newMode && setValue('actualAttritionMode', newMode);
            handleFetchActualReport();
          }
        }, {
          children: [_jsx(FormControlLabel, {
            "data-testid": "actual-attrition-percentage",
            value: ACTUAL_ATTRITION_MODE.PERCENTAGE,
            control: _jsx(Radio, {
              size: "small"
            }),
            label: t('inputMode.percentage')
          }), _jsx(FormControlLabel, {
            "data-testid": "actual-attrition-absolute",
            value: ACTUAL_ATTRITION_MODE.ABSOLUTE,
            control: _jsx(Radio, {
              size: "small"
            }),
            label: t('inputMode.absolute')
          })]
        })), _jsxs(StyledToggleButtonGroup, __assign({
          value: frequency,
          exclusive: true,
          onChange: function (_e, newFrequency) {
            newFrequency && setValue('frequency', newFrequency);
            handleFetchActualReport();
          }
        }, {
          children: [_jsx(ToggleButton, __assign({
            value: FREQUENCY_ENUM.PERIOD
          }, {
            children: isMonthlyPlanType ? t('frequency.monthly') : t('frequency.weekly')
          })), _jsx(ToggleButton, __assign({
            value: FREQUENCY_ENUM.DAILY,
            disabled: metricTab === METRICS_ENUM.ATTRITION || dailyDisabled
          }, {
            children: t('frequency.daily')
          }))]
        }))]
      }))]
    }))]
  });
};